<template>
  <button v-if="!isSubscribed" class="ms-lg-2 btn btn-outline-info" @click="subscribeToNotifications" :disabled="isLoading">
    <!-- ✅ ИСПРАВЛЕНО: Добавлен индикатор загрузки -->
    <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
    {{ isLoading ? t('buttons.loading') : t('buttons.subscribeUser') }}
  </button>
  <button v-else class="ms-lg-2 btn btn-outline-info" @click="unsubscribeUser" :disabled="isLoading">
    <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
    {{ isLoading ? t('buttons.loading') : t('buttons.unsubcribeUser') }}
  </button>
</template>

<script setup>
import {ref, onMounted} from 'vue'
import {useStore} from 'vuex'
import {useI18n} from 'vue-i18n'
import axios from '@/axios.config'
import {log} from "@/utils/clientLogger.js";
import {showToast} from "@/utils/swiperToast";

const {t} = useI18n({useScope: 'global'})
const store = useStore()
const isSubscribed = ref(false)
const isLoading = ref(false) // ✅ ДОБАВЛЕНО: состояние загрузки

// ✅ ИСПРАВЛЕНО: Добавлена обработка всех возможных ошибок
const subscribeToNotifications = async () => {
  if (isLoading.value) return;

  isLoading.value = true;

  try {
    // ✅ ИСПРАВЛЕНО: Проверка поддержки браузером
    if (!('Notification' in window)) {
      throw new Error(t('errors.notificationNotSupported'));
    }

    if (!('serviceWorker' in navigator)) {
      throw new Error(t('errors.serviceWorkerNotSupported'));
    }

    if (!('PushManager' in window)) {
      throw new Error(t('errors.pushManagerNotSupported'));
    }

    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      throw new Error(t('errors.notificationRequestPermission'));
    }

    // ✅ ИСПРАВЛЕНО: Ждем готовности service worker с timeout
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Service Worker timeout')), 10000)
      )
    ]);

    // ✅ ИСПРАВЛЕНО: Проверяем существующую подписку
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      log.info('View:SubscribeNotification:subscribeToNotifications', {
        message: 'Existing subscription found'
      });
      await sendSubscriptionToServer(subscription);
      return;
    }

    // Создаем новую подписку
    const publicKey = process.env.VUE_APP_WEB_PUSH_PUBLIC_KEY;

    if (!publicKey) {
      throw new Error('Public key not configured');
    }

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    await sendSubscriptionToServer(subscription);

    log.info('View:SubscribeNotification:subscribeToNotifications', {
      message: 'Successfully subscribed'
    });

  } catch (error) {
    log.error('View:SubscribeNotification:subscribeToNotifications', {error: error.message});

    // ✅ ИСПРАВЛЕНО: Показываем понятное сообщение пользователю
    let errorMessage = t('errors.subscriptionFailed');

    if (error.message.includes('permission')) {
      errorMessage = t('errors.notificationPermissionRequired');
    } else if (error.message.includes('timeout')) {
      errorMessage = t('errors.serviceWorkerTimeout');
    }

    showToast(errorMessage,'error');

  } finally {
    isLoading.value = false;
  }
};

// ✅ ИСПРАВЛЕНО: Улучшена обработка отписки
const unsubscribeUser = async () => {
  if (isLoading.value) return;

  isLoading.value = true;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      log.warn('View:SubscribeNotification:unsubscribeUser', {
        message: 'No subscription found'
      });
      isSubscribed.value = false;
      return;
    }

    // ✅ ИСПРАВЛЕНО: Сначала отписываемся в браузере
    const unsubscribed = await subscription.unsubscribe();

    if (!unsubscribed) {
      throw new Error('Failed to unsubscribe');
    }

    log.info('View:SubscribeNotification:unsubscribeUser', {
      message: 'Successfully unsubscribed from browser'
    });

    // ✅ ИСПРАВЛЕНО: Затем удаляем с сервера
    const subscriptionId = localStorage.getItem('webPushSubscription');

    if (subscriptionId) {
      try {
        await axios.delete(`/api/notifications/subscription/${subscriptionId}`);
        log.info('View:SubscribeNotification:unsubscribeUser', {
          message: 'Successfully deleted from server'
        });
      } catch (error) {
        // ✅ ИСПРАВЛЕНО: Не падаем если подписка уже удалена на сервере
        if (error.response?.status !== 404) {
          log.error('View:SubscribeNotification:unsubscribeUser', {
            error: error.message
          });
        }
      }

      localStorage.removeItem('webPushSubscription');
    }

    isSubscribed.value = false;

  } catch (error) {
    log.error('View:SubscribeNotification:unsubscribeUser', {error: error.message});
    alert(t('errors.unsubscribeFailed') || 'Failed to unsubscribe');

  } finally {
    isLoading.value = false;
  }
};

// ✅ ИСПРАВЛЕНО: Добавлена обработка ошибок и retry логика
const sendSubscriptionToServer = async (subscription, retryCount = 0) => {
  const MAX_RETRIES = 3;

  try {
    const userId = store.state.user._id;

    if (!userId) {
      throw new Error('User ID not found');
    }

    const res = await axios.post('/api/notifications/subscribe', {
      userId,
      subscription: subscription.toJSON()
    });

    if (!res.data || !res.data.data._id) {
      throw new Error('Invalid server response');
    }

    localStorage.setItem('webPushSubscription', res.data.data._id);
    isSubscribed.value = true;

    log.info('View:SubscribeNotification:sendSubscriptionToServer', {
      message: 'Subscription saved to server',
      subscriptionId: res.data.data._id
    });

  } catch (error) {
    log.error('View:SubscribeNotification:sendSubscriptionToServer', {
      error: error.message,
      retryCount
    });

    // ✅ ИСПРАВЛЕНО: Retry логика для сетевых ошибок
    if (retryCount < MAX_RETRIES && error.response?.status >= 500) {
      log.info('View:SubscribeNotification:sendSubscriptionToServer', {
        message: 'Retrying...',
        attempt: retryCount + 1
      });

      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return sendSubscriptionToServer(subscription, retryCount + 1);
    }

    throw error;
  }
};

// ✅ ИСПРАВЛЕНО: Улучшена проверка подписки
const checkSubscription = async () => {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      log.info('View:SubscribeNotification:checkSubscription', {
        message: 'Push notifications not supported'
      });
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    isSubscribed.value = !!subscription;

    // ✅ ИСПРАВЛЕНО: Синхронизация с localStorage
    const savedId = localStorage.getItem('webPushSubscription');

    if (subscription && !savedId) {
      // Есть подписка в браузере, но нет в localStorage - сохраняем на сервер
      await sendSubscriptionToServer(subscription);
    } else if (!subscription && savedId) {
      // Нет подписки в браузере, но есть в localStorage - очищаем
      localStorage.removeItem('webPushSubscription');
    }

    log.info('View:SubscribeNotification:checkSubscription', {
      isSubscribed: isSubscribed.value,
      hasLocalStorageId: !!savedId
    });

  } catch (error) {
    log.error('View:SubscribeNotification:checkSubscription', {error: error.message});
  }
};

const urlBase64ToUint8Array = (base64String) => {
  try {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  } catch (error) {
    log.error('View:SubscribeNotification:urlBase64ToUint8Array', {error: error.message});
    throw new Error('Invalid public key format');
  }
};

onMounted(() => {
  checkSubscription();
});
</script>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}
</style>